import { format } from 'date-fns';

import { Button } from '@/components/Buttons';
import HabitatLink from '@/components/HabitatLink';
import { Video, VideoV2 } from '@/queries/media/types';
import { useSelector } from '@/redux/helper';
import { selectHasExplorerFeature } from '@/redux/selectors/users';
import { trackMP } from '@/shared/mp';

import HabitatProfileImageLink from './HabitatProfileImageLink';

interface HomeHighlightVideoInfoBarProps {
  highlight: Video | VideoV2;
  onNextHighlight?: () => void;
}

const HomeHighlightVideoInfoBar = ({ highlight, onNextHighlight }: HomeHighlightVideoInfoBarProps) => {
  const isFreeUser = !useSelector(selectHasExplorerFeature);
  const videoHabitat = 'habitat' in highlight ? highlight.habitat : (highlight as Video).habitatId;
  const type = 'videoType' in highlight ? highlight.videoType : highlight.type;
  const username = 'user' in highlight ? highlight.user?.username : (highlight as Video).username;
  const { publishedAt } = highlight;

  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === videoHabitat?._id));

  const trackVisitHabitatClick = () => {
    if (habitat) {
      trackMP('visit-habitat-link-clicked', {
        habitatId: habitat._id,
        entryPoint: 'highlight-info-bar',
      });
    }
  };

  return (
    <div className="relative z-10 flex items-center justify-between bg-primary-green px-4 py-3 text-white desktop:bg-white desktop:px-6 desktop:py-5 desktop:text-black desktop:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)]">
      {habitat?.zoo && (
        <div className="shrink-0 pb-4 mobile:hidden">
          <HabitatProfileImageLink
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            profileImage={habitat.profileImage}
            title={habitat.title}
            zooLogo={habitat.zoo.logo}
            zooName={habitat.zoo.name}
            entryPoint="highlight-info-bar"
          />
        </div>
      )}
      <div className="flex min-w-0 grow flex-col pr-2 desktop:pl-8">
        {habitat?.zoo && (
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a text-xl font-medium leading-none text-grey-1 hover:text-grey-3 mobile:hidden"
            onClick={trackVisitHabitatClick}
          >
            {habitat.title}
          </HabitatLink>
        )}
        {habitat?.zoo && habitat.animal && (
          <p className="preflight preflight-p mt-[7px] text-[10px] font-bold uppercase leading-[11px] text-stone-500 mobile:hidden">
            {habitat.animal} AT {habitat.zoo.name}
          </p>
        )}
        <h4 className="preflight preflight-h4 min-w-0 text-sm font-bold desktop:hidden">
          <span className="line-clamp-1 min-w-0 truncate" data-testid="highlight-title-info-bar-mobile">
            {highlight.title || 'Highlight'}
          </span>
        </h4>
        <div className="mt-3 text-base leading-[21px] mobile:hidden">
          <div>
            <span className="text-black" data-testid="highlight-title-info-bar-desktop">{`\u201C${
              highlight.title || 'Highlight'
            }\u201D taken by `}</span>
            {type === 'curated' ? (
              <span className="text-primary-green">@zoolife</span>
            ) : (
              <a
                href={`/u/${username || 'zoolife'}`}
                className="preflight preflight-a text-primary-green hover:text-secondary-green"
              >
                @{username || 'zoolife'}
              </a>
            )}
          </div>
          {publishedAt && <div className="text-grey-6">{format(new Date(publishedAt), 'd MMMM, haaa')}</div>}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {habitat?.zoo && (
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a flex items-center justify-center rounded-full border border-white px-3 py-1 text-center text-xs font-medium text-white hover:bg-white/10 desktop:border-transparent desktop:bg-primary-green desktop:px-4 desktop:py-2 desktop:text-sm desktop:font-medium desktop:text-white desktop:hover:bg-secondary-green"
            onClick={trackVisitHabitatClick}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {!(type === 'clip' && isFreeUser) && onNextHighlight && (
          <Button variant="outline" onClick={onNextHighlight}>
            Next Clip
          </Button>
        )}
      </div>
    </div>
  );
};

export default HomeHighlightVideoInfoBar;
